import React, { useEffect, useRef } from 'react';
import { Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft, faCheck } from '@fortawesome/free-solid-svg-icons';
import AddIrrigation from '../../views/AddIrrigation';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import api from '../../api/api';
import { getUserId } from "../../utils/auth";

const AddIrrigationWrapper = ({ wizardData, completeStep, onPrevious, onNext, resetAllData, isWizardMode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formInitialized = useRef(false);

  const irrigationTypeMap = {
    'drip': 'drip',
    'furrow': 'furrow',
    'Irrigation à la raie': 'furrow',
    'Irrigation goutte à goutte (bande humide)': 'Goutte',
    'Irrigation par planche': 'furrow',
    'irrigation par planche (submersion)': 'furrow',
    'lateral': 'lateral',
    'Micro-aspersion': 'Micro-aspersion',
    'pivot': 'pivot',
    'sdi': 'sdi',
    'sprinkler': 'sprinkler',
    'Goutte': 'Goutte'
  };

  const [irrigData, setIrrigData] = React.useState({});
  // Add state for crop types
  const [listCrop, setListCrop] = React.useState([]);

  // 1. Améliorer l'initialisation des données
  useEffect(() => {
    // Récupérer les données enregistrées précédemment
    if (wizardData?.step4?.data) {
      // S'assurer que crop_uid est une chaîne
      const crop_uid = wizardData.step4.data.crop_uid ? String(wizardData.step4.data.crop_uid) : "";
      
      setIrrigData(prev => ({
        ...prev,
        ...wizardData.step4.data,
        // Garantir que zone_uid et crop_uid sont bien définis
        zone_uid: wizardData.step4.data.zone_uid || wizardData.zoneId || "",
        crop_uid: crop_uid
      }));
    } else {
      // Initialiser avec les données du wizard même sans données d'étape
      setIrrigData(prev => ({
        ...prev,
        zone_uid: wizardData.zoneId || "",
        crop_uid: ""
      }));
    }
  }, [wizardData]);

  // Add effect to load crop types
  useEffect(() => {
    const getCropTypes = async () => {
      try {
        const response = await api.get('/croptype/list-crop');
        if (response && response.data && response.data.Croptype) {
          setListCrop(response.data.Croptype);
        }
      } catch (error) {
        console.error("Error loading crop types:", error);
      }
    };
    
    getCropTypes();
  }, []);

  useEffect(() => {
    const wasRecentlySubmitted = localStorage.getItem('irrigationFormRecentlySubmitted');
    const resetAllForms = localStorage.getItem('resetAllForms');
    
    if (wasRecentlySubmitted || resetAllForms) {
      // Réinitialiser le formulaire d'irrigation
      setIrrigData({});
      
      localStorage.removeItem('irrigationFormRecentlySubmitted');
      // Ne pas supprimer resetAllForms ici pour permettre aux autres composants de le lire
    }
  }, []);

  // Mettre à jour les données d'irrigation
const handleIrrigChange = (newIrrigData) => {
  setIrrigData(prevData => ({
    ...prevData,  // Préserver toutes les valeurs précédentes
    ...newIrrigData, // Appliquer les nouvelles valeurs
    irrigType: newIrrigData.irrigType || prevData.irrigType,
    // Conserver explicitement crop_uid si elle existe déjà mais n'est pas dans les nouvelles données
    crop_uid: newIrrigData.crop_uid || prevData.crop_uid || ""
  }));
};

  // 2. Ajouter un gestionnaire pour sauvegarder les données avant de quitter
  const handlePrevious = () => {
    // Sauvegarder les données actuelles avant de naviguer
    if (irrigData && Object.keys(irrigData).length > 0) {
      completeStep(4, irrigData);
    }
    
    // Puis naviguer
    if (onPrevious) {
      onPrevious();
    }
  };

  // Gérer la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Récupération de la valeur du type d'irrigation par différentes méthodes
    let irrigType = irrigData.irrigType;

    // Si irrigType est vide, essayer de le récupérer d'autres sources
    if (!irrigType && wizardData?.step4?.data?.irrigType) {
      irrigType = wizardData.step4.data.irrigType;
      // Mettre à jour l'état local pour cohérence
      setIrrigData(prev => ({ ...prev, irrigType }));
    }

    // Vérifier que le type d'irrigation est défini
    if (!irrigType) {
      swal(t("Error"), t("Please select an irrigation type"), "error");
      return;
    }

    // Ajoute le mapping ici
    const mappedType = irrigationTypeMap[irrigType];

    if (!mappedType) {
      swal(t("Error"), t("Please select an irrigation type"), "error");
      return;
    }

    try {
      // Récupérer les cultures disponibles pour cette zone
      const cropCheck = await api.post('/zone', { zone_uid: irrigData.zone_uid || wizardData.zoneId });
      const availableCrops = cropCheck.data?.zone?.field?.crops || [];

      // Si irrigData.crop_uid est un UUID complet, l'utiliser directement
      // Sinon, chercher par cropTypeId
      let selectedCropUid = irrigData.crop_uid || "";
      let selectedCropFound = false;

      // Vérifier si selectedCropUid est déjà un UUID valide qui existe dans availableCrops
      if (typeof selectedCropUid === 'string' && selectedCropUid.includes("-")) {
        selectedCropFound = availableCrops.some(crop => crop.uid === selectedCropUid);
      }

      // Si ce n'est pas un UUID valide ou n'existe pas, chercher par cropTypeId
      if (!selectedCropFound) {
        const cropTypeIdToFind = parseInt(irrigData.crop_uid) || wizardData.cropTypeId;

        if (cropTypeIdToFind) {
          const matchingCrop = availableCrops.find(crop =>
            parseInt(crop.croptype_id) === cropTypeIdToFind
          );

          if (matchingCrop) {
            selectedCropUid = matchingCrop.uid;
            selectedCropFound = true;
          }
        }
      }

      // Vérification finale pour s'assurer qu'on a bien une valeur
      if (!selectedCropFound || !selectedCropUid) {
        if (availableCrops.length > 0) {
          const cropNames = availableCrops.map(crop => {
            const cropType = listCrop.find(ct => ct.id == crop.croptype_id)?.crop || crop.croptype_id;
            return `${cropType} (ID: ${crop.uid})`;
          }).join(', ');

          return swal({
            title:  t("Please select a crop for this zone"),
            text: `${t("Available crops")}: ${cropNames}`,
            icon: "warning",
          });
        } else {
          return swal(t("Error"), t("No crops available for this zone. Please add a crop first."), "error");
        }
      }

      // Préparer les données pour l'API
      const apiData = {
        type: mappedType, // Utilise la valeur mappée ici
        zone_uid: irrigData.zone_uid || wizardData.zoneId,
        crop_uid: selectedCropUid, // Utiliser la culture trouvée
        flowrate: irrigData.flowrate || "",
        irrigated_already: irrigData.irrigated_already || "",
        name: irrigData.name || "",
        pivot_shape: irrigData.pivot_shape || "",
        irrigation_syst: irrigData.irrigation_syst || "",
        pivot_length: irrigData.pivot_length || "",
        pivot_coord: irrigData.pivot_coord || "",
        full_runtime: irrigData.full_runtime || "",
        lateral: irrigData.lateral || "",
        drippers: irrigData.drippers || "",
        effIrrig: irrigData.effIrrig || "",
        pumpFlow: irrigData.pumpFlow || "",
        pumpType: irrigData.pumpType || "",
        lines_number: irrigData.linesNumber || "",
        drippers_spacing: irrigData.drippersSpacing || ""
      };

      // Appel API principal
      const response = await api.post('/irrigation/add-irrigation', apiData);

      if (response.data.type === "success") {
        localStorage.setItem('irrigationFormRecentlySubmitted', 'true');
        completeStep(4, irrigData);

        swal({
          title: t("success"),
          text: t("All_info_submitted"),
          icon: "success",
          buttons: [t("Return_to_fields"), t("Finish")]
        }).then(() => {
          const userId = getUserId();
          localStorage.removeItem(`smartFarmWizardData_${userId}`);
          localStorage.setItem('resetAllForms', 'true');
          navigate('/AddField');
        });
      } else {
        swal(t("Error"), response.data.message || t("Failed to add irrigation information"), "error");
      }
    } catch (error) {
      console.error("API error details:", error.response?.data);

      if (error.response?.data?.message === "no_crop_found") {
        swal({
          title: t("Error: Crop Not Found"),
          text: t("The selected crop could not be found in this zone. Please select a different crop that belongs to this zone."),
          icon: "error"
        });
      } else {
        swal(t("Error"), t("Failed to add irrigation information. Please check your inputs and try again."), "error");
      }
    }
  };

  return (
    <div className="wizard-step-content p-4">
      <Form onSubmit={handleSubmit}>
         <AddIrrigation
          isWizardMode={true}
          fieldId={wizardData.fieldId}
          fieldName={wizardData.fieldName}
          zoneId={wizardData.zoneId}
          zoneName={wizardData.zoneName}
          cropId={wizardData.cropTypeId} 
          cropName={wizardData.cropTypeName}
          formData={irrigData}
          onChange={handleIrrigChange}
          onSubmit={() => {}}
        />
        <div className="d-flex justify-content-between mt-4">
          {/* 3. Remplacer le bouton Previous par notre version */}
          <Button variant="outline-secondary" onClick={handlePrevious} className="bg-white">
            <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
            {t('previous')}
          </Button>
          <Button type="submit" className="btn-success">
            <FontAwesomeIcon icon={faCheck} className="me-2" />
            {t('Finish')}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddIrrigationWrapper;
